import React, { useEffect, useState } from "react";
import Dropdown from "components/dropdown";
import { IoMdNotificationsOutline } from "react-icons/io";
import { 
  BsArrowBarUp, 
  BsInfoCircle, 
  BsExclamationCircle, 
  BsCheckCircle,
  BsClock,
  BsTrash,
  BsBell,
  BsBellFill
} from "react-icons/bs";
import { socket } from "api";
import { fetchNotifications, markNotificationAsRead, getUserProfile } from "api";

const Notification = () => {
  const [notifications, setNotifications] = useState([]);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socketError, setSocketError] = useState(null);
  const [hasUnread, setHasUnread] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userProfile = await getUserProfile();
        if (userProfile && userProfile.user && userProfile.user.id) {
          setUserId(userProfile.user.id);
          const fetchedNotifications = await fetchNotifications(userProfile.user.id);
          setNotifications(fetchedNotifications);
          setHasUnread(fetchedNotifications.some(notification => !notification.read));
        } else {
          console.error("User ID not found in profile.");
        }
      } catch (error) {
        console.error("Error fetching user profile or notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();

    // Use centralized socket from api.js
    if (userId) {
      socket.emit('joinRoom', userId);

      socket.on('notification', (notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setHasUnread(true);
      });

      return () => {
        socket.off('notification');
      };
    }
  }, [userId]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications((prev) => {
        const updated = prev.map(notif => 
          notif.id === notificationId 
            ? {...notif, read: true} 
            : notif
        );
        // Update hasUnread based on the updated notifications
        setHasUnread(updated.some(notification => !notification.read));
        return updated;
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      await Promise.all(
        unreadNotifications.map((notif) => markNotificationAsRead(notif.id))
      );
      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
      setHasUnread(false);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "team_creation":
        return <BsCheckCircle className="h-5 w-5" />;
      case "team_role_change":
        return <BsInfoCircle className="h-5 w-5" />;
      case "team_points_earned":
        return <BsArrowBarUp className="h-5 w-5" />;
      case "team_creation_failed":
        return <BsExclamationCircle className="h-5 w-5" />;
      default:
        return <BsBell className="h-5 w-5" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case "team_creation":
        return "from-green-400 to-green-500";
      case "team_role_change":
        return "from-blue-400 to-blue-500";
      case "team_points_earned":
        return "from-yellow-400 to-orange-500";
      case "team_creation_failed":
        return "from-red-400 to-red-500";
      default:
        return "from-gray-400 to-gray-500";
    }
  };

  const formatTimeAgo = (createdAt) => {
    if (!createdAt) return "Just now";
    
    const now = new Date();
    const created = new Date(createdAt);
    const diffInMs = now - created;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return created.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const displayedNotifications = showAll ? notifications : notifications.slice(0, 3);
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <Dropdown
      button={
        <div className="relative cursor-pointer group">
          <div className="p-2 rounded-lg transition-all duration-200 group-hover:bg-gray-100 dark:group-hover:bg-navy-700">
            {hasUnread ? (
              <BsBellFill className="h-5 w-5 text-orange-500 dark:text-orange-400" />
            ) : (
              <IoMdNotificationsOutline className="h-5 w-5 text-gray-600 dark:text-white group-hover:text-orange-500 dark:group-hover:text-orange-400" />
            )}
          </div>
          {hasUnread && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-5 w-5 bg-orange-500 text-white text-xs font-bold items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </span>
          )}
        </div>
      }
      animation="origin-[65%_0%] md:origin-top-right transition-all duration-300 ease-in-out"
      children={
        <div className="flex w-[300px] flex-col rounded-[20px] bg-white shadow-xl dark:!bg-navy-800 dark:text-white overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-orange-400 to-orange-600">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/20">
                <BsBell className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-base font-bold text-white">
                  Notifications
                </p>
                <p className="text-xs text-white/80">
                  {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                </p>
              </div>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="px-2 py-1 text-xs font-medium text-white bg-white/20 rounded-md hover:bg-white/30 transition-colors duration-200"
              >
                Clear all
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-[320px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-dashed border-orange-500"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-navy-700 mb-2">
                  <BsBell className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  No notifications
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  You're all caught up!
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {displayedNotifications.map((notification, index) => (
                  <div
                    key={notification.id}
                    className={`group relative p-3 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-navy-700 cursor-pointer ${
                      !notification.read ? 'bg-orange-50 dark:bg-orange-500/10' : ''
                    }`}
                    onClick={() => !notification.read && handleMarkAsRead(notification.id)}
                  >
                    {/* Unread indicator */}
                    {!notification.read && (
                      <div className="absolute top-3 left-3 w-2 h-2 bg-orange-500 rounded-full"></div>
                    )}
                    
                    <div className="flex gap-3 ml-3">
                      {/* Icon */}
                      <div className={`flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-r ${getNotificationColor(notification.type)} text-white flex-shrink-0`}>
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white leading-tight">
                            {notification.title}
                          </p>
                          <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                            <BsClock className="h-3 w-3" />
                            {formatTimeAgo(notification.createdAt)}
                          </div>
                        </div>
                        
                        <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {notification.message}
                        </p>
                        
                        {/* Subtle action indicator */}
                        {!notification.read && (
                          <p className="text-xs text-orange-500 dark:text-orange-400 mt-1 font-medium">
                            Click to mark as read
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 3 && (
            <div className="border-t border-gray-100 dark:border-gray-700 p-2 bg-gray-50 dark:bg-navy-700">
              <button
                onClick={() => setShowAll(!showAll)}
                className="w-full text-center text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-orange-500 dark:hover:text-orange-400 transition-colors duration-200"
              >
                {showAll ? 'Show less' : `Show ${notifications.length - 3} more`}
              </button>
            </div>
          )}

          {/* Socket Error */}
          {socketError && (
            <div className="p-3 bg-red-50 dark:bg-red-500/10 border-t border-red-200 dark:border-red-500/20">
              <p className="text-xs text-red-600 dark:text-red-400 text-center">
                {socketError}
              </p>
            </div>
          )}
        </div>
      }
      classNames={"py-2 top-4 -left-[260px] md:-left-[280px] w-max"}
    />
  );
};

export default Notification;
